<script setup>
import { ref } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  modelValue: { type: String, required: true },
  options: { type: Array, required: true },
  customUrl: { type: String, default: '' },
  customName: { type: String, default: '' },
  error: { type: String, default: '' },
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'upload', 'remove']);
const fileInput = ref(null);

const copy = {
  title: 'Papel de parede',
  description: 'Personalize o fundo da área de conversa.',
  localTitle: 'Imagem local',
  localDescription:
    'Fica somente neste navegador e não é enviada para a Talkvia.',
  choose: 'Escolher imagem',
  replace: 'Trocar imagem',
  remove: 'Remover',
  formats: 'JPG, PNG ou WebP · máximo de 5 MB',
  customLabel: 'Minha imagem',
  selected: 'Selecionado',
  options: 'opções',
};

const openFilePicker = () => fileInput.value?.click();
const handleFile = event => {
  const [file] = event.target.files || [];
  if (file) emit('upload', file);
  event.target.value = '';
};
</script>

<template>
  <section
    class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2"
  >
    <div
      class="flex flex-col gap-3 border-b border-n-weak bg-n-alpha-1 p-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-image" class="size-5" />
        </span>
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ copy.title }}
          </h3>
          <p class="text-xs text-n-slate-10">{{ copy.description }}</p>
        </div>
      </div>
      <span
        class="inline-flex w-fit items-center rounded-full bg-n-solid-1 px-2.5 py-1 text-[11px] font-semibold text-n-slate-10 ring-1 ring-n-weak"
      >
        {{ options.length }} {{ copy.options }}
      </span>
    </div>

    <div class="p-5">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <button
          v-for="option in options"
          :key="option.key"
          type="button"
          class="group relative overflow-hidden rounded-2xl border bg-n-solid-1 p-2.5 text-left transition-all"
          :class="
            modelValue === option.key
              ? 'border-n-brand ring-2 ring-n-brand/15'
              : 'border-n-weak hover:-translate-y-0.5 hover:border-n-slate-7 hover:shadow-md'
          "
          :aria-pressed="modelValue === option.key"
          @click="emit('update:modelValue', option.key)"
        >
          <div
            class="relative h-20 overflow-hidden rounded-xl border border-black/10"
            :style="
              option.bg
                ? {
                    backgroundColor: option.bg,
                    backgroundImage: option.pattern || 'none',
                    backgroundRepeat: option.repeat || 'repeat',
                    backgroundSize: option.size || 'auto',
                  }
                : {}
            "
            :class="!option.bg ? 'bg-n-surface-2' : ''"
          >
            <div
              v-if="!option.bg"
              class="grid h-full place-content-center text-n-slate-8"
            >
              <Icon icon="i-lucide-ban" class="size-5" />
            </div>
            <div v-else class="absolute inset-x-2 bottom-2 space-y-1.5">
              <span
                class="block h-3 w-3/5 rounded-full bg-white/85 shadow-sm"
              />
              <span
                class="ml-auto block h-3 w-2/5 rounded-full bg-white/60 shadow-sm"
              />
            </div>
          </div>
          <div class="mt-2 flex items-center justify-between gap-2 px-0.5">
            <span class="truncate text-xs font-semibold text-n-slate-11">
              {{ option.label }}
            </span>
            <Icon
              v-if="modelValue === option.key"
              icon="i-lucide-circle-check"
              class="size-4 shrink-0 text-n-brand"
            />
          </div>
        </button>
      </div>

      <div
        class="mt-5 grid gap-4 rounded-2xl border border-dashed border-n-strong bg-n-alpha-1 p-4 sm:grid-cols-[12rem_minmax(0,1fr)]"
      >
        <button
          type="button"
          class="relative h-32 overflow-hidden rounded-xl border bg-n-solid-1 transition"
          :class="
            modelValue === 'custom'
              ? 'border-n-brand ring-2 ring-n-brand/15'
              : 'border-n-weak hover:border-n-slate-7'
          "
          :disabled="!customUrl"
          :aria-pressed="modelValue === 'custom'"
          @click="emit('update:modelValue', 'custom')"
        >
          <span
            v-if="customUrl"
            class="absolute inset-0 bg-cover bg-center"
            :style="{ backgroundImage: `url(${customUrl})` }"
          />
          <span
            v-else
            class="absolute inset-0 grid place-content-center text-n-slate-8"
          >
            <Icon icon="i-lucide-image-plus" class="size-8" />
          </span>
          <span
            v-if="modelValue === 'custom'"
            class="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-n-brand px-2 py-1 text-[10px] font-semibold text-white shadow"
          >
            <Icon icon="i-lucide-check" class="size-3" />
            {{ copy.selected }}
          </span>
        </button>

        <div class="flex min-w-0 flex-col justify-center">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-hard-drive" class="size-4 text-n-brand" />
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ copy.localTitle }}
            </h4>
          </div>
          <p class="mt-1 text-xs leading-5 text-n-slate-10">
            {{ copy.localDescription }}
          </p>
          <p v-if="customName" class="mt-1 truncate text-xs text-n-slate-11">
            {{ customName }}
          </p>
          <p v-else class="mt-1 text-[11px] text-n-slate-9">
            {{ copy.formats }}
          </p>
          <p v-if="error" class="mt-2 text-xs font-medium text-n-ruby-11">
            {{ error }}
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
              :disabled="busy"
              @click="openFilePicker"
            >
              <Icon icon="i-lucide-upload" class="size-4" />
              {{ customUrl ? copy.replace : copy.choose }}
            </button>
            <button
              v-if="customUrl"
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-xs font-semibold text-n-slate-11 transition hover:border-n-ruby-8 hover:text-n-ruby-11"
              :disabled="busy"
              @click="emit('remove')"
            >
              <Icon icon="i-lucide-trash-2" class="size-4" />
              {{ copy.remove }}
            </button>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="sr-only"
            accept="image/jpeg,image/png,image/webp"
            @change="handleFile"
          />
        </div>
      </div>
    </div>
  </section>
</template>
